import React, { useState, useEffect } from 'react';
import { 
  Building, 
  MapPin, 
  Phone, 
  Mail, 
  Globe, 
  CreditCard, 
  User, 
  Hash, 
  Briefcase, 
  Upload, 
  X, 
  CheckCircle, 
  AlertCircle
} from 'lucide-react';
import { companyProfileAPI } from '../services/api';

const CompanyProfilePage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [formData, setFormData] = useState({
    company_name: '',
    address: '',
    postal_code: '',
    city: '',
    phone: '',
    email: '',
    website: '',
    bank_name: '',
    account_holder: '',
    iban: '',
    swift_bic: '',
    tax_number: '',
    mwst_number: ''
  });
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [existingLogo, setExistingLogo] = useState('');

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    setLoading(true);
    try {
      const response = await companyProfileAPI.getProfile();
      const profile = response.data.profile;
      
      // Formulardaten mit Profildaten füllen
      setFormData({
        company_name: profile.company_name || '',
        address: profile.address || '',
        postal_code: profile.postal_code || '',
        city: profile.city || '',
        phone: profile.phone || '',
        email: profile.email || '',
        website: profile.website || '',
        bank_name: profile.bank_name || '',
        account_holder: profile.account_holder || '',
        iban: profile.iban || '',
        swift_bic: profile.swift_bic || '',
        tax_number: profile.tax_number || '',
        mwst_number: profile.mwst_number || ''
      });
      
      // Logo-URL setzen, falls vorhanden
      if (profile.logo_url) {
        setExistingLogo(profile.logo_url);
      }
      
      setMessage({ type: '', text: '' });
    } catch (err) {
      if (err.response && err.response.status === 404) {
        // Kein Profil gefunden ist in Ordnung, der Benutzer kann eines erstellen
        setMessage({ type: '', text: '' });
      } else {
        console.error('Fehler beim Laden des Profils:', err);
        setMessage({ 
          type: 'error', 
          text: 'Fehler beim Laden des Unternehmensprofils'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogo(file);
      
      // Vorschau erstellen
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = async () => {
    if (existingLogo) {
      if (window.confirm('Möchten Sie das Logo wirklich löschen?')) {
        try {
          await companyProfileAPI.deleteLogo();
          setExistingLogo('');
          setMessage({ 
            type: 'success', 
            text: 'Logo erfolgreich gelöscht'
          });
        } catch (err) {
          console.error('Fehler beim Löschen des Logos:', err);
          let errorMessage = 'Fehler beim Löschen des Logos';
          
          if (err.response && err.response.data && err.response.data.error) {
            errorMessage += `: ${err.response.data.error}`;
          }
          
          setMessage({ 
            type: 'error', 
            text: errorMessage
          });
        }
      }
    } else {
      // Nur das ausgewählte, aber noch nicht gespeicherte Logo entfernen
      setLogo(null);
      setLogoPreview('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      // FormData für den Upload mit Datei verwenden
      const data = new FormData();
      
      // Formulardaten hinzufügen
      Object.keys(formData).forEach(key => {
        data.append(key, formData[key]);
      });
      
      // Logo hinzufügen, falls ausgewählt
      if (logo) {
        data.append('logo', logo);
      }
      
      // Profil aktualisieren oder erstellen
      let response;
      try {
        response = await companyProfileAPI.updateProfile(data);
      } catch (updateError) {
        if (updateError.response && updateError.response.status === 404) {
          // Wenn kein Profil gefunden, erstellen wir ein neues
          response = await companyProfileAPI.createProfile(data);
        } else {
          throw updateError;
        }
      }
      
      // UI aktualisieren
      if (response.data.profile.logo_url) {
        setExistingLogo(response.data.profile.logo_url);
        setLogoPreview('');
      }
      setLogo(null);
      
      setMessage({ 
        type: 'success', 
        text: 'Unternehmensprofil erfolgreich gespeichert'
      });
      
      // Nach kurzer Zeit die Nachricht ausblenden
      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 5000);
    } catch (err) {
      console.error('Fehler beim Speichern des Profils:', err);
      
      let errorMessage = 'Unbekannter Fehler';
      
      if (err.response) {
        if (err.response.data && err.response.data.error) {
          errorMessage = err.response.data.error;
        } else {
          errorMessage = `Server-Fehler (${err.response.status})`;
        }
        console.log('Server-Antwortdaten:', err.response.data);
      } else if (err.request) {
        errorMessage = 'Keine Antwort vom Server erhalten. Überprüfe deine Internetverbindung.';
      } else {
        errorMessage = err.message;
      }
      
      setMessage({ 
        type: 'error', 
        text: `Fehler beim Speichern: ${errorMessage}`
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
        <Building className="mr-3 h-6 w-6 text-primary-600" />
        Unternehmensprofil
      </h1>
      
      {message.text && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.type === 'error' 
            ? 'bg-red-50 border-l-4 border-red-500 text-red-700' 
            : 'bg-green-50 border-l-4 border-green-500 text-green-700'
        }`}>
          <div className="flex">
            {message.type === 'error' ? (
              <AlertCircle className="h-5 w-5 mr-2" />
            ) : (
              <CheckCircle className="h-5 w-5 mr-2" />
            )}
            <span>{message.text}</span>
          </div>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="md:col-span-2">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <Briefcase className="mr-2 h-5 w-5 text-primary-600" />
              Unternehmensdaten
            </h2>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Firmenname *</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Building className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                required
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Ihre Firma GmbH"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Adresse *</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MapPin className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                required
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Musterstraße 123"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Postleitzahl *</label>
            <input
              type="text"
              name="postal_code"
              value={formData.postal_code}
              onChange={handleChange}
              required
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="12345"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ort *</label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              required
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Musterstadt"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Phone className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="+41 12 345 67 89"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">E-Mail</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="info@ihrefirma.ch"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Globe className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleChange}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="https://www.ihrefirma.ch"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">MwSt-Nummer</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Hash className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                name="mwst_number"
                value={formData.mwst_number}
                onChange={handleChange}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="CHE-123.456.789 MWST"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Steuer-/UID-Nummer</label>
            <input
              type="text"
              name="tax_number"
              value={formData.tax_number}
              onChange={handleChange}
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="CHE-123.456.789"
            />
          </div>
          
          <div className="md:col-span-2">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <CreditCard className="mr-2 h-5 w-5 text-primary-600" />
              Bankdaten
            </h2>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bankname</label>
            <input
              type="text"
              name="bank_name"
              value={formData.bank_name}
              onChange={handleChange}
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Musterbank AG"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Kontoinhaber</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <User className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                name="account_holder"
                value={formData.account_holder}
                onChange={handleChange}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Ihre Firma GmbH"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">IBAN</label>
            <input
              type="text"
              name="iban"
              value={formData.iban}
              onChange={handleChange}
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="CH00 0000 0000 0000 0000 0"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">BIC/SWIFT</label>
            <input
              type="text"
              name="swift_bic"
              value={formData.swift_bic}
              onChange={handleChange}
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="EXAMPLEXXX"
            />
          </div>
          
          <div className="md:col-span-2">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <Upload className="mr-2 h-5 w-5 text-primary-600" />
              Firmenlogo
            </h2>
            
            <div className="flex flex-col space-y-4">
              {/* Logo-Vorschau */}
              {(existingLogo || logoPreview) && (
                <div className="relative border p-4 rounded-lg bg-gray-50">
                  <button
                    type="button"
                    onClick={removeLogo}
                    className="absolute top-2 right-2 bg-red-100 text-red-600 p-1 rounded-full hover:bg-red-200"
                    title="Logo entfernen"
                  >
                    <X className="h-4 w-4" />
                  </button>
                  
                  <div className="flex justify-center">
                    <img 
                      src={logoPreview || existingLogo} 
                      alt="Firmenlogo" 
                      className="max-h-40 object-contain"
                    />
                  </div>
                </div>
              )}
              
              {/* Logo-Upload */}
              <div className="mt-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Logo hochladen (max. 5MB, JPG, PNG oder SVG)</label>
                <div className="flex items-center justify-center w-full">
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <Upload className="w-8 h-8 mb-3 text-gray-400" />
                      <p className="mb-2 text-sm text-gray-500">
                        <span className="font-semibold">Klicken</span> oder Datei hier ablegen
                      </p>
                      <p className="text-xs text-gray-500">SVG, PNG oder JPG (max. 5MB)</p>
                    </div>
                    <input 
                      id="logo-upload" 
                      type="file" 
                      className="hidden" 
                      accept=".jpg,.jpeg,.png,.svg" 
                      onChange={handleLogoChange}
                    />
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <button
            type="submit"
            disabled={saving}
            className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${saving ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            {saving ? (
              <>
                <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                Speichern...
              </>
            ) : (
              'Profil speichern'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CompanyProfilePage;
